<script setup lang="ts">
  import { IconAccount } from '@gat-ship/icons'
  import type { TenantUser } from '@gat-ship/types'
  import { testIds } from '@gat-ship/types'
  import {
    Button,
    FormField,
    FormSubmit,
    isValidPhoneNumber,
    Modal,
    ModalContent,
    ModalFooter,
    ModalHeader,
    ModalHeaderDescription,
    ModalHeaderIcon,
    ModalHeaderTitle,
    ModalSection,
    InputGroupPhone,
    useForm,
    z
  } from '@gat-ship/vue-components'
  import { FieldGroup } from '@gat-ship/vue-components'
  import { ref } from 'vue'
  import { useTenantUserDetailContext } from '../../composables/useTenantUserDetail'

  // Preserve the phone-format validation the old FormPhoneField applied on
  // blur: empty is allowed (field is optional), a non-empty value must be a
  // valid phone number.
  const phoneValidators = {
    onBlur: ({ value }: { value: string }) =>
      !value || value.length === 0
        ? undefined
        : isValidPhoneNumber(value)
          ? undefined
          : 'Invalid phone number'
  }

  const isOpen = ref(false)
  const tenantUserProvider = useTenantUserDetailContext()!
  const modalTitle = ref('Change Phone Number')
  const description = ref('')

  const changePhoneNumberSchema = z.object({
    userId: z.string().min(1),
    mobilePhoneNumber: z.string().optional()
  })
  type ChangePhoneNumberFormValues = z.infer<typeof changePhoneNumberSchema>

  const form = useForm({
    defaultValues: {
      userId: '',
      mobilePhoneNumber: undefined
    } as ChangePhoneNumberFormValues,
    validators: {
      onChange: changePhoneNumberSchema
    },
    onSubmit: async ({ value }) => {
      await tenantUserProvider.commands.changePhoneNumber({
        userId: value.userId,
        mobilePhoneNumber: value.mobilePhoneNumber || null
      })
      isOpen.value = false
    }
  })

  export interface ShowChangePhoneNumberModalParams {
    tenantUser: TenantUser
  }

  function showModal({ tenantUser }: ShowChangePhoneNumberModalParams) {
    modalTitle.value = tenantUser.mobilePhone
      ? 'Change Phone Number'
      : 'Add Phone Number'
    description.value = tenantUser.fullName
    form.reset({
      userId: tenantUser.userId,
      mobilePhoneNumber: tenantUser.mobilePhone
    })
    isOpen.value = true
  }

  defineExpose({ showModal })
</script>

<template>
  <modal v-model:open="isOpen">
    <ModalHeader>
      <ModalHeaderTitle>
        <ModalHeaderIcon>
          <IconAccount class="size-5" :stroke-width="1.5" />
        </ModalHeaderIcon>
        {{ modalTitle }}
      </ModalHeaderTitle>
      <ModalHeaderDescription v-if="description">
        {{ description }}
      </ModalHeaderDescription>
    </ModalHeader>
    <ModalContent>
      <ModalSection>
        <FieldGroup>
          <FormField
            :form="form"
            name="mobilePhoneNumber"
            label="Phone Number"
            :validators="phoneValidators"
          >
            <template #default="{ field }">
              <InputGroupPhone
                :id="field.name"
                :model-value="field.state.value ?? undefined"
                :data-testid="
                  testIds.tenantUser.modal.changePhoneNumber.mobilePhoneInput
                "
                @update:model-value="(v) => field.handleChange(v ?? '')"
                @blur="field.handleBlur"
              />
            </template>
          </FormField>
        </FieldGroup>
      </ModalSection>
    </ModalContent>
    <ModalFooter>
      <FormSubmit :form="form" label="Save Changes" />
      <Button variant="ghost" @click="isOpen = false"> Cancel </Button>
    </ModalFooter>
  </modal>
</template>
